import React, { useEffect, useState } from 'react';
import { useSpring, animated } from '@react-spring/web';
import tw from 'twin.macro';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const Container = tw.div`bg-gradient-to-r from-blue-500 to-teal-400 px-8 py-4 rounded-lg border border-gray-200 shadow-lg flex flex-col items-center h-screen`;
const Title = tw.h1`text-4xl font-bold text-white mb-10`;
const Conn = tw.div`flex flex-col sm:flex-row w-full h-auto items-center justify-between space-x-6 px-4 sm:px-8 py-3 sm:py-6`;
const VideoContainer = tw(animated.div)`rounded-lg border mb-8 sm:mb-0 w-full h-80 max-w-screen-md overflow-hidden`;
const VideoFrame = tw(animated.iframe)`w-full h-full`;
const FormContainer = tw(animated.div)`w-full max-w-screen-md bg-white rounded-lg shadow-xl p-8 flex flex-col items-center sm:items-start`;
const FormControl = tw(animated.div)`w-full mb-6`;
const Label = tw.label`text-lg font-medium text-gray-700 mb-2 block`;
const Input = tw.input`p-3 border border-gray-300 rounded-lg focus:(outline-none ring-2 ring-teal-500) transition duration-200 w-full`;
const ErrorText = tw.span`text-red-500 text-sm mt-2`;
const Button = tw(animated.button)`bg-teal-600 text-white px-8 py-3 rounded-lg font-semibold uppercase tracking-wider transition duration-300 transform hover:(bg-teal-700 scale-105) focus:(outline-none ring-2 ring-teal-500 ring-offset-2 ring-offset-blue-500) disabled:(opacity-50 cursor-not-allowed)`;
const LinkText = tw.a`text-teal-100 underline hover:text-teal-200 transition duration-200`;
const StatusText = tw.p`text-black text-lg mt-4`;

const ApiKeyGenerator = () => {
  const [apiKey, setApiKey] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [apiKeyError, setApiKeyError] = useState('');
  const [secretKeyError, setSecretKeyError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [userDetails, setUserDetails] = useState(null);
  const navigate = useNavigate();

  // Function to get the token from localStorage
  const getToken = () => localStorage.getItem('token');


  // Create an axios instance with the token
  const axiosInstance = axios.create({
    baseURL: 'https://falcon-algo.onrender.com/api',
    headers: {
      'Authorization': `Bearer ${getToken()}`
    }
  });

  useEffect(() => {
    // Fetch user details and existing API key and secret key if they exist
    axiosInstance.get('/api-key')
      .then(response => {
        setUserDetails(response.data);
        setApiKey(response.data.apiKey || '');
        setSecretKey(response.data.secretKey || '');
      })
      .catch(error => {
        if (error.response && error.response.status === 401) {
          // Token is invalid or expired
           // Redirect to login page
        } else {
          console.error('Error fetching user details:', error);
        }
      });
  }, [navigate]);

  useEffect(() => {
    if (statusMessage) {
      const timer = setTimeout(() => {
        setStatusMessage('');
      }, 2000);
      return () => clearTimeout(timer); // Cleanup timer on unmount or when statusMessage changes
    }
  }, [statusMessage]);

  const handleNextClick = () => {
    // Validate inputs
    let valid = true;
    if (apiKey === '') {
      setApiKeyError('API Key is required');
      valid = false;
    } else {
      setApiKeyError('');
    }
    if (secretKey === '') {
      setSecretKeyError('Secret Key is required');
      valid = false;
    } else {
      setSecretKeyError('');
    }

    if (valid) {
      // Save API key and secret key
      axiosInstance.post('/api-key', { apiKey, secretKey })
        .then(response => {
          setStatusMessage('API Key and Secret Key saved successfully!');
          setApiKeyError('');
          setSecretKeyError('');
        })
        .catch(error => {
          console.error('Error saving API key:', error);
          setStatusMessage('Error saving API Key and Secret Key.');
        });
    } else {
      setStatusMessage('');
    }
  };

  const titleProps = useSpring({ from: { opacity: 0, transform: 'translateY(-20px)' }, to: { opacity: 1, transform: 'translateY(0)' }, delay: 300 });
  const videoProps = useSpring({ from: { opacity: 0, transform: 'translateX(-20px)' }, to: { opacity: 1, transform: 'translateX(0)' }, delay: 500 });
  const formProps = useSpring({ from: { opacity: 0, transform: 'translateX(20px)' }, to: { opacity: 1, transform: 'translateX(0)' }, delay: 700 });
  const buttonHoverProps = useSpring({ from: { transform: 'scale(1)' }, to: async (next) => {
    while (1) {
      await next({ transform: 'scale(1.05)' });
      await next({ transform: 'scale(1)' });
    }
  }, reset: true });

  return (
    <animated.div style={formProps}>
      <Container>
        <Title style={titleProps}>Generate ICICI Direct Breeze API Keys</Title>
        <Conn>
          <VideoContainer style={videoProps}>
            <VideoFrame
              src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1"
              title="API Key Generation Video"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></VideoFrame>
          </VideoContainer>
          <FormContainer style={formProps}>
            <FormControl>
              <Label htmlFor="apiKey">API Key</Label>
              <Input 
                type="text" 
                id="apiKey" 
                value={apiKey} 
                onChange={(e) => setApiKey(e.target.value)} 
                placeholder="Enter your API Key" 
              />
              {apiKeyError && <ErrorText>{apiKeyError}</ErrorText>}
            </FormControl>
            <FormControl>
              <Label htmlFor="secretKey">Secret Key</Label>
              <Input 
                type="text" 
                id="secretKey" 
                value={secretKey} 
                onChange={(e) => setSecretKey(e.target.value)} 
                placeholder="Enter your Secret Key" 
              />
              {secretKeyError && <ErrorText>{secretKeyError}</ErrorText>}
            </FormControl>
            <Button 
              style={buttonHoverProps} 
              onClick={handleNextClick}
              disabled={!apiKey || !secretKey}
            >
              Save
            </Button>
        {statusMessage && <StatusText>{statusMessage}</StatusText>}
          </FormContainer>
        </Conn>
        <p className="text-center text-white">
          To generate your API Key and Secret Key, please visit{' '}
          <LinkText href="https://api.icicidirect.com/apiuser/home" target="_blank" rel="noopener noreferrer">
            ICICI Direct Breeze API
          </LinkText>
        </p>
      </Container>
    </animated.div>
  );
};

export default ApiKeyGenerator;
